import { gql, type TypedDocumentNode } from '@apollo/client';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove.ts';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { UnclaimedDeliverableItemOrderButton } from '#src/components/rewards/deliverableItem/UnclaimedDeliverableItemOrders/UnclaimedDeliverableItemOrder/index.tsx';

import { ConnectTaskWidget } from '../ConnectTaskWidget/index.tsx';
import { DailyActionTaskWidget } from '../DailyActionTaskWidget/index.tsx';
import { FeaturedCollectionTaskWidget } from '../FeaturedCollectionTaskWidget/index.tsx';
import { PlayMissionWidget } from '../PlayMissionWidget/index.tsx';
import { RaffleWidget } from '../RaffleWidget/index.tsx';
import type {
  SeriesMissionsWidgets_cardSetRoot,
  SeriesMissionsWidgets_currentUser,
  SeriesMissionsWidgets_deliverableItemOrder,
} from './__generated__/index.graphql.ts';

type Props = {
  currentUser: SeriesMissionsWidgets_currentUser;
  cardSet: SeriesMissionsWidgets_cardSetRoot;
  deliverableItemOrder?: SeriesMissionsWidgets_deliverableItemOrder;
  showConnectTaskDialog: boolean;
  onConnectTaskDialogClose: () => void;
  onConnectTaskDialogOpen: () => void;
};
export const SeriesMissionsWidgets = ({
  currentUser,
  cardSet,
  deliverableItemOrder,
  showConnectTaskDialog,
  onConnectTaskDialogClose,
  onConnectTaskDialogOpen,
}: Props) => {
  const isDesktop = useIsDesktopAndAbove();

  const claimableCollectionTasks = (
    currentUser?.setCollectionsTaskTracks || []
  ).filter(
    task =>
      isType(task, 'TasksTrack') &&
      task.currentStep?.aasmState === ManagerProgressionTaskState.COMPLETED
  ).length;

  const playDailyActionTask = isType(
    currentUser?.dailyRunTask,
    'DailyActionTask'
  )
    ? currentUser?.dailyRunTask
    : null;

  const featuredCollectionTask = currentUser?.setCollectionsTaskTracks?.at(0);

  const featuredPlayTask = currentUser?.setPlayTasks?.at(0);

  const inviteTasks = (currentUser?.inviteTasksAsReferrer || []).filter(
    isTypeFn('InviteTaskAsReferrer')
  );

  const { currentRaffle } = cardSet;

  return (
    <Vertical center gap={2}>
      {!!deliverableItemOrder && !isDesktop && (
        <UnclaimedDeliverableItemOrderButton
          deliverableItemOrder={deliverableItemOrder}
          hideText
        />
      )}
      {!!currentRaffle && !isDesktop && <RaffleWidget raffle={currentRaffle} />}
      {!!playDailyActionTask && !isDesktop && (
        <DailyActionTaskWidget dailyActionTask={playDailyActionTask} />
      )}
      {!!featuredCollectionTask && !isDesktop && (
        <Badge badgeContent={claimableCollectionTasks} overlap="circular">
          <FeaturedCollectionTaskWidget
            featuredCollectionTask={featuredCollectionTask}
            hasClaimableCollectionTasks={claimableCollectionTasks > 0}
          />
        </Badge>
      )}
      {!!featuredPlayTask && !isDesktop && (
        <PlayMissionWidget task={featuredPlayTask} />
      )}
      {!!inviteTasks.length && (
        <ConnectTaskWidget
          currentUser={currentUser}
          cardSet={cardSet}
          open={showConnectTaskDialog}
          onClose={onConnectTaskDialogClose}
          onOpen={onConnectTaskDialogOpen}
        />
      )}
    </Vertical>
  );
};

SeriesMissionsWidgets.fragments = {
  currentUser: gql`
    fragment SeriesMissionsWidgets_currentUser on CurrentUser {
      slug
      dailyRunTask(sport: $sport) {
        id
        name
        aasmState
        ... on DailyActionTask {
          id
          ...DailyActionTaskWidget_dailyActionTask
        }
      }
      setCollectionsTaskTracks(sport: $sport) {
        id
        ... on TasksTrack {
          id
          currentStep {
            id
            aasmState
          }
        }
        ...FeaturedCollectionTaskWidget_taskInterface
      }
      setPlayTasks(sport: $sport) {
        id
        ...PlayMissionWidget_taskInterface
      }
      ...ConnectTaskWidget_currentUser
    }
    ${DailyActionTaskWidget.fragments.dailyActionTask}
    ${FeaturedCollectionTaskWidget.fragments.taskInterface}
    ${PlayMissionWidget.fragments.taskInterface}
    ${ConnectTaskWidget.fragments.currentUser}
  ` as TypedDocumentNode<SeriesMissionsWidgets_currentUser>,
  cardSet: gql`
    fragment SeriesMissionsWidgets_cardSetRoot on CardSetRoot {
      id
      currentRaffle(sport: $sport) {
        slug
        ...RaffleWidget_raffle
      }
      ...ConnectTaskWidget_cardSetRoot
    }
    ${ConnectTaskWidget.fragments.cardSetRoot}
    ${RaffleWidget.fragments.raffle}
  ` as TypedDocumentNode<SeriesMissionsWidgets_cardSetRoot>,
  deliverableItemOrder: gql`
    fragment SeriesMissionsWidgets_deliverableItemOrder on DeliverableItemOrder {
      id
      ...UnclaimedDeliverableItemOrderButton_deliverableItemOrder
    }
    ${UnclaimedDeliverableItemOrderButton.fragments.deliverableItemOrder}
  ` as TypedDocumentNode<SeriesMissionsWidgets_deliverableItemOrder>,
};
